// src/components/warranties/WarrantyPhotosGallery.jsx
import React, { useMemo, useState } from "react";
import {
    Box,
    Card,
    CardActionArea,
    CardMedia,
    Dialog,
    IconButton,
    Stack,
    Typography,
    Tooltip,
} from "@mui/material";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";

import { deleteObject, ref as storageRef, refFromURL } from "firebase/storage";
import { storage } from "../../utils/firebase/firebase";

function normalizePhotos(rawOrList) {
    // se já veio array do backend
    if (Array.isArray(rawOrList)) {
        return rawOrList
            .map((x) => {
                if (!x) return null;
                if (typeof x === "string") return x.trim();
                if (typeof x === "object") return String(x.url || x.downloadURL || x.href || "").trim();
                return null;
            })
            .filter(Boolean);
    }

    // string "1 por linha"
    return String(rawOrList || "")
        .split("\n")
        .map((s) => s.trim())
        .filter((s) => s && s.startsWith("http"));
}


export default function WarrantyPhotosGallery({
    photosRaw,                 // string com 1 url por linha
    title = "Imagens anexadas",
    emptyText = "Nenhuma imagem anexada ainda.",
    thumbSize = 120,

    // ✅ obrigatório p/ remover da UI (atualiza o state do form)
    onRemoveUrl,               // (url: string) => void

    // ✅ opcional p/ deletar do Storage (fortemente recomendado)
    // Se você passar esse callback e retornar true, a galeria remove da UI também.
    onDeleteFromStorage,       // async (url: string) => boolean

    // UX
    confirmDelete = true,
}) {

    const urls = useMemo(
        () => normalizePhotos(photosRaw).filter((u) => typeof u === "string" && u.startsWith("http")),
        [photosRaw]
    );


    const [open, setOpen] = useState(false);
    const [activeUrl, setActiveUrl] = useState("");

    const [deletingUrl, setDeletingUrl] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingDeleteUrl, setPendingDeleteUrl] = useState("");

    const handleOpen = (url) => {
        setActiveUrl(url);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setActiveUrl("");
    };

    const requestDelete = (url) => {
        if (!confirmDelete) return handleDelete(url);
        setPendingDeleteUrl(url);
        setConfirmOpen(true);
    };

    const handleDelete = async (url) => {
        if (!url) return;

        try {
            setDeletingUrl(url);

            // 1) tenta deletar do Storage, se o callback existir
            if (typeof onDeleteFromStorage === "function") {
                const ok = await onDeleteFromStorage(url);
                if (!ok) return;
            }

            // 2) remove da UI sempre
            if (typeof onRemoveUrl === "function") onRemoveUrl(url);

            // se estava aberta em fullscreen, fecha
            if (activeUrl === url) handleClose();
        } finally {
            setDeletingUrl("");
            setConfirmOpen(false);
            setPendingDeleteUrl("");
        }
    };

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography fontWeight={900}>{title}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {urls.length} arquivo(s)
                </Typography>
            </Stack>

            {!urls.length ? (
                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                    {emptyText}
                </Typography>
            ) : (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: `repeat(auto-fill, minmax(${thumbSize}px, 1fr))`,
                        gap: 1.25,
                    }}
                >
                    {urls.map((url, idx) => (
                        <Card
                            key={`${url}_${idx}`}
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                overflow: "hidden",
                                position: "relative",
                            }}
                        >
                            {/* Botão delete no thumbnail */}
                            <Tooltip title="Excluir imagem">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            requestDelete(url);
                                        }}
                                        disabled={deletingUrl === url}
                                        sx={{
                                            position: "absolute",
                                            top: 6,
                                            right: 6,
                                            zIndex: 2,
                                            color: "white",
                                            bgcolor: "rgba(0,0,0,0.45)",
                                            "&:hover": { bgcolor: "rgba(0,0,0,0.60)" },
                                            "&.Mui-disabled": { opacity: 0.5, color: "white" },
                                        }}
                                    >
                                        <DeleteRoundedIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <CardActionArea onClick={() => handleOpen(url)}>
                                <CardMedia
                                    component="img"
                                    image={url}
                                    alt={`Foto ${idx + 1}`}
                                    onError={async (e) => {
                                        const src = e?.currentTarget?.currentSrc || e?.currentTarget?.src;
                                        console.error("Falha ao carregar imagem (img tag):", src);

                                        try {
                                            const res = await fetch(src, { method: "GET", mode: "cors" });
                                            console.error("Imagem fetch status:", res.status, res.statusText);

                                            // alguns headers podem vir null por CORS, mas status vem
                                            console.error("Response content-type:", res.headers.get("content-type"));
                                        } catch (err) {
                                            console.error("Fetch também falhou:", err);
                                        }
                                    }}
                                    sx={{ width: "100%", height: thumbSize, objectFit: "cover", display: "block" }}
                                />
                            </CardActionArea>
                        </Card>
                    ))}
                </Box>
            )}

            {/* Fullscreen viewer */}
            <Dialog
                open={open}
                onClose={handleClose}
                fullScreen
                PaperProps={{ sx: { bgcolor: "rgba(0,0,0,0.95)" } }}
            >
                <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            zIndex: 2,
                            color: "white",
                            bgcolor: "rgba(255,255,255,0.12)",
                            "&:hover": { bgcolor: "rgba(255,255,255,0.18)" },
                        }}
                    >
                        <CloseRoundedIcon />
                    </IconButton>

                    {/* delete também em fullscreen */}
                    {!!activeUrl && (
                        <IconButton
                            onClick={() => requestDelete(activeUrl)}
                            disabled={deletingUrl === activeUrl}
                            sx={{
                                position: "absolute",
                                top: 12,
                                right: 60,
                                zIndex: 2,
                                color: "white",
                                bgcolor: "rgba(239,68,68,0.18)",
                                "&:hover": { bgcolor: "rgba(239,68,68,0.28)" },
                                "&.Mui-disabled": { opacity: 0.5, color: "white" },
                            }}
                        >
                            <DeleteRoundedIcon />
                        </IconButton>
                    )}

                    {activeUrl ? (
                        <Box
                            component="img"
                            src={activeUrl}
                            alt="Imagem em tela cheia"
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                display: "block",
                            }}
                        />
                    ) : null}
                </Box>
            </Dialog>

            {/* Confirmação simples (Dialog) */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <Box sx={{ p: 2.5, minWidth: 320 }}>
                    <Typography fontWeight={900} sx={{ mb: 1 }}>
                        Excluir imagem?
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        Isso vai remover a imagem da garantia{typeof onDeleteFromStorage === "function" ? " e apagar do Storage" : ""}.
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }} justifyContent="flex-end">
                        <IconButton onClick={() => setConfirmOpen(false)} sx={{ borderRadius: 2 }}>
                            <CloseRoundedIcon />
                        </IconButton>

                        <IconButton
                            onClick={() => handleDelete(pendingDeleteUrl)}
                            sx={{
                                borderRadius: 2,
                                bgcolor: "rgba(239,68,68,0.18)",
                                "&:hover": { bgcolor: "rgba(239,68,68,0.28)" },
                            }}
                        >
                            <DeleteRoundedIcon />
                        </IconButton>
                    </Stack>
                </Box>
            </Dialog>
        </Box>
    );
}
